import { Icon, IconButton } from '@aibees/shared/components';
import { useBoolean } from '@aibees/shared/hooks';
import { useTableSearchItemStore } from '@aibees/shared/store/table-search-item';
import { SxStyle } from '@aibees/shared/theme';
import { classnames, ColumnSize, getColumnWidth } from '@aibees/shared/utils';
import { Box, Collapse, TableCell } from '@mui/material';
import { mergeSx } from 'merge-sx';
import { memo, useCallback, useMemo } from 'react';
import { SELECTABLE_COLUMN_WIDTH } from '../../constant';
import { useAdvanceTableStickyContext } from '../../context';
import { AdvanceTableColumn } from '../../types';
import * as Styled from './styles';
import * as SX from './sx';
import type { TableRowProps as Props } from './types';

export const TableRow = memo(
  ({
    className,
    item,
    index,
    selected,
    config,
    columns,
    onSelect,
    onRowClick,
    checkboxEnabled,
    ...other
  }: Props) => {
    const expanded = useBoolean();
    const { selectedItem } = useTableSearchItemStore();
    const { stickyRowsHeight } = useAdvanceTableStickyContext();

    const rowKey = config?.rowKey ?? 'id';
    const isSelected =
      (selectedItem && selectedItem === item[rowKey]) || selected;
    const hasStickyCol = useMemo(
      () => columns.some((col) => col.isSticky),
      [columns]
    );

    const handleExpandClick = useCallback(
      () => expanded.setValue('toggle'),
      [expanded]
    );

    const handleSelectChange = useCallback(() => {
      onSelect?.(index, item[rowKey], !selected, item);
    }, [onSelect, index, item, rowKey, selected]);

    const handleRowClick = useCallback(
      (_e: React.MouseEvent) => {
        onRowClick?.(item, item[rowKey]);
      },
      [onRowClick, item, rowKey]
    );

    const memoizedIcon = useMemo(
      () => <Icon name={expanded.value ? 'minus' : 'plus'} size={20} />,
      [expanded.value]
    );

    const renderCellContent = useCallback(
      (col: AdvanceTableColumn) => {
        const value = item[col.id as string];
        const content = col.render
          ? col.render(value, item, index)
          : value ?? '-';
        if (col.expandable) {
          return (
            <Box display="flex" alignItems="center">
              <IconButton
                aria-label="expand row"
                size="small"
                icon={memoizedIcon}
                onClick={handleExpandClick}
                disableRipple
                sx={SX.expandButton}
              />
              {content}
            </Box>
          );
        }
        return content;
      },
      [expanded.value, handleExpandClick, item, index]
    );

    const extraContent = useMemo(
      () =>
        columns
          .filter((col) => col.expandable && col.extraContentRender)
          .map((col) => col.extraContentRender?.(item, index)),
      [columns, item, index]
    );

    const calculatedMinWidth = (size?: ColumnSize) => {
      return size ? getColumnWidth(size) : 'auto';
    };

    return (
      <>
        <Styled.Wrapper
          className={classnames('TableRow', className)}
          {...other}
          onClick={handleRowClick}
          sx={mergeSx(
            SX.row(isSelected),
            SX.rowCollaps({ stickyCollapsed: false, stickyRowsHeight })
          )}
        >
          {config.rowSelectable && (
            <Styled.CheckboxCell
              className="TableRowSelectCell"
              onClick={(e) => e.stopPropagation()}
              sx={hasStickyCol ? SX.getStickyCheckboxSx(isSelected) : undefined}
            >
              <Box className="hover-border" sx={SX.hoverBorder(isSelected)} />
              <Styled.SelectBox
                size="small"
                checked={selected}
                onChange={handleSelectChange}
                disabled={!selected && !checkboxEnabled}
              />
            </Styled.CheckboxCell>
          )}

          {columns.map((col) => {
            const stickySx = col.isSticky
              ? SX.getStickyCellSx({
                  isSelected,
                  width: SELECTABLE_COLUMN_WIDTH,
                })
              : ({} as SxStyle);
            const sx = mergeSx(
              stickySx,
              SX.cellBase({ col, isSelected, cellSx: config.cellSx }),
              { maxWidth: calculatedMinWidth(col.size) }
            );
            return (
              <Styled.Cell
                className={col.className}
                key={String(col.key ?? col.id)}
                data-id={col.key ?? col.id}
                sx={sx}
              >
                {renderCellContent(col)}
              </Styled.Cell>
            );
          })}
        </Styled.Wrapper>

        {extraContent.length > 0 && (
          <TableCell
            style={{ paddingBottom: 0, paddingTop: 0 }}
            colSpan={columns.length + 1}
          >
            <Collapse in={expanded.value} timeout="auto" unmountOnExit>
              {extraContent}
            </Collapse>
          </TableCell>
        )}
      </>
    );
  }
);

TableRow.displayName = 'TableRow';
